import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import InputIcon from '@mui/icons-material/Input';
import ListIcon from '@mui/icons-material/List';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    Box,
    CssBaseline,
    Divider,
    Drawer as MuiDrawer,
    IconButton,
    List, ListItem, ListItemButton, ListItemIcon, ListItemText,
    Tooltip,
    type CSSObject, type Theme, styled, useTheme,
} from '@mui/material';

import Log from '@/Logger';
import { FeatureContext } from '../../FeatureContext';
import { Drawer_Width } from '../constance';
import { getUrlQueryParams, isOAuthRedirectFromKintone } from '../util/kintone/index';
import { useConfirmDialog } from '../util/useConfirmDialog';

function strEnum<T extends string>(o: Array<T>): { [K in T]: K } {
    return o.reduce((res, key) => {
        res[key] = key;
        return res;
    }, Object.create(null));
}

/* eslint-disable @typescript-eslint/no-redeclare */
const Mode = strEnum(['Download', 'Upload', 'RecycleBin', 'Config']);
type Mode = keyof typeof Mode;
/* eslint-enable @typescript-eslint/no-redeclare */

type Props = {
    drawerOpen: boolean;
    handleDrawerClose: () => void;
    header: React.ReactNode;
    documentList: React.ReactNode;
    sendDocument: React.ReactNode;
    config: React.ReactNode;
    recycleBin: React.ReactNode;
    setIsPdfEditVisible?: React.Dispatch<React.SetStateAction<boolean>>;
    isConflict?: boolean;
    setIsConflict?: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function Drawer(props: Props) {
    const logger = Log.getLogger();
    const theme = useTheme();
    const [mode, setMode] = React.useState<Mode>(() => {
        if (isOAuthRedirectFromKintone(getUrlQueryParams())) {
            return Mode.Config;
        }
        return Mode.Download;
    });

    const [activeDlPdfMenuColor, setDlPdfMenuColor] = React.useState('#dcdcdc');
    const [activeUlPdfMenuColor, setUlPdfMenuColor] = React.useState('transparent');
    const [activeBinMenuColor, setBinMenuColor] = React.useState('transparent');
    const [activeCfgMenuColor, setCfgMenuColor] = React.useState('transparent');

    const [ConfirmDialog, { openConfirmDialog, setConfirmDialogMessage }] = useConfirmDialog();

    const headerRef = useRef<HTMLDivElement>(null);
    const drawerRef = useRef<HTMLDivElement>(null);

    const features = useContext(FeatureContext);

    // メインコンテンツの高さをヘッダとフッタの高さに応じて変化させる
    const [mainContentHeight, setMainContentHeight] = useState<string>('100%');

    const handleResize = useCallback(() => { // windowサイズに変更があった場合、MainContentHeightを再計算する
        const headerHeight = headerRef.current ? getComputedStyle(headerRef.current).height : undefined;
        const footerHeight = '20px'; // Footerの高さは固定(index.css)
        setMainContentHeight(headerHeight ? `calc(100% - ${headerHeight} - ${footerHeight})` : '100%');
    }, []);
    useEffect(() => { // リサイズイベントを登録する
        window.addEventListener('resize', handleResize);
        // クリーンアップ
        return () => (window.removeEventListener('resize', handleResize));
    }, [handleResize]);
    useLayoutEffect(handleResize, [handleResize]); // 初回マウント時に1回だけ実行する

    // 設定内で使うプロパティと干渉しないために新しいプロパティを定義
    const [localQueryParams, setLocalQueryParams] = useState(getUrlQueryParams());

    const handleSwitchPage = (mode: Mode) => {
        return () => {
            logger.ui.info('Drawer', mode);

            if (props.isConflict) {//pdf編集画面からの移動で、競合が起きている場合には警告を出す
                setConfirmDialogMessage([`このまま画面を移動すると、競合した内容が保存されません。`, `移動してもよろしいですか？`], true);
                openConfirmDialog().then((result) => {
                    switch (result) {
                        case 'confirm':
                            // ユーザーが画面移動を選択した場合
                            props.setIsPdfEditVisible?.(false); // pdf編集画面を非表示にする
                            props.setIsConflict?.(false); // ConfirmDialog表示のフラグをオフ

                            setDlPdfMenuColor(Mode.Download === mode ? '#dcdcdc' : 'transparent');
                            setUlPdfMenuColor(Mode.Upload === mode ? '#dcdcdc' : 'transparent');
                            setBinMenuColor(Mode.RecycleBin === mode ? '#dcdcdc' : 'transparent');
                            setCfgMenuColor(Mode.Config === mode ? '#dcdcdc' : 'transparent');
                            setMode(mode);
                            break;
                        case 'cancel':
                            // ユーザーが画面移動をキャンセルした場合
                            props.setIsPdfEditVisible?.(true); // pdf編集画面を表示する
                            setMode(Mode.Download); // ドキュメント一覧に戻る
                            break;
                        default:
                            // 通常は通らないはずの分岐
                            // ConfirmDialogResultの変更を検知するための型チェックであり、コンパイルエラー時はswitch文を見直す必要があります。
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            const _exhaustiveCheck: never = result;
                            break;
                    }
                });

            } else {
                props.setIsPdfEditVisible?.(false);

                setDlPdfMenuColor(Mode.Download === mode ? '#dcdcdc' : 'transparent');
                setUlPdfMenuColor(Mode.Upload === mode ? '#dcdcdc' : 'transparent');
                setBinMenuColor(Mode.RecycleBin === mode ? '#dcdcdc' : 'transparent');
                setCfgMenuColor(Mode.Config === mode ? '#dcdcdc' : 'transparent');
                setMode(mode);
            }
        };
    };

    function switchPage() {
        if (isOAuthRedirectFromKintone(localQueryParams)) { // kintoneからリダイレクトされた場合には設定ページに移る
            logger.ui.info('Drawer', 'decideFirstPage', 'Moved from kintone OAuth');
            // queryParamsを空にする(無限ループ対策)
            setLocalQueryParams({});
            handleSwitchPage(Mode.Config)();
            return props.config;
        }

        if (mode === Mode.Download) return props.documentList;
        if (mode === Mode.Upload) return props.sendDocument;
        if (mode === Mode.RecycleBin) return props.recycleBin;
        if (mode === Mode.Config) return props.config;

        return props.documentList;
    }

    return (
        <Box className='Drawer'
            sx={{
                // 横並びにする
                display: 'flex',
                // 画面いっぱいに広げる
                height: '100vh',
                width: '100vw',
            }}
        >{/* Drawerの枠組み */}

            <CssBaseline />
            {/* ヘッダはstaticに浮かせる */}
            {props.header}
            {/* ドロワー */}
            <CustomDrawer ref={drawerRef} variant="permanent" open={props.drawerOpen}>
                <DrawerHeader>
                    <IconButton onClick={props.handleDrawerClose}>
                        {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </DrawerHeader>
                <Divider />
                <List>
                    <ListItem disablePadding sx={{ display: 'block' }}>
                        <Tooltip title='ドキュメント一覧' placement='right'>
                            <span>
                                <ListItemButton
                                    onClick={handleSwitchPage(Mode.Download)}
                                    sx={{ backgroundColor: activeDlPdfMenuColor, minHeight: 48, justifyContent: props.drawerOpen ? 'initial' : 'center' }}
                                >
                                    <ListItemIcon sx={{ minWidth: 0, mr: props.drawerOpen ? 3 : 'auto', justifyContent: 'center' }}>
                                        <ListIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={'ドキュメント一覧'} sx={{ opacity: props.drawerOpen ? 1 : 0 }} />
                                </ListItemButton>
                            </span>
                        </Tooltip>
                        <Tooltip title='ドキュメント送信' placement='right'>
                            <span>
                                <ListItemButton
                                    onClick={handleSwitchPage(Mode.Upload)}
                                    sx={{ display: features.sendDocument() ? '' : 'none', backgroundColor: activeUlPdfMenuColor, minHeight: 48, justifyContent: props.drawerOpen ? 'initial' : 'center' }}
                                >
                                    <ListItemIcon sx={{ minWidth: 0, mr: props.drawerOpen ? 3 : 'auto', justifyContent: 'center' }}>
                                        <InputIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={'ドキュメント送信'} sx={{ opacity: props.drawerOpen ? 1 : 0 }} />
                                </ListItemButton>
                            </span>
                        </Tooltip>
                        <Tooltip title='ゴミ箱' placement='right'>
                            <span>
                                <ListItemButton
                                    onClick={handleSwitchPage(Mode.RecycleBin)}
                                    sx={{ display: features.deleteDocument() ? '' : 'none', backgroundColor: activeBinMenuColor, minHeight: 48, justifyContent: props.drawerOpen ? 'initial' : 'center' }}
                                >
                                    <ListItemIcon sx={{ minWidth: 0, mr: props.drawerOpen ? 3 : 'auto', justifyContent: 'center' }}>
                                        <DeleteIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={'ゴミ箱'} sx={{ opacity: props.drawerOpen ? 1 : 0 }} />
                                </ListItemButton>
                            </span>
                        </Tooltip>
                        <Tooltip title='設定' placement='right'>
                            <span>
                                <ListItemButton
                                    onClick={handleSwitchPage(Mode.Config)}
                                    sx={{ backgroundColor: activeCfgMenuColor, minHeight: 48, justifyContent: props.drawerOpen ? 'initial' : 'center' }}
                                >
                                    <ListItemIcon sx={{ minWidth: 0, mr: props.drawerOpen ? 3 : 'auto', justifyContent: 'center' }}>
                                        <SettingsIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={'設定'} sx={{ opacity: props.drawerOpen ? 1 : 0 }} />
                                </ListItemButton>
                            </span>
                        </Tooltip>
                    </ListItem>
                </List>
            </CustomDrawer>
            {/* メインコンテンツ */}
            <Box component="main"
                sx={{
                    height: '100%',
                    // 幅はDrawerの開閉に応じて変化させる
                    width: (drawerRef.current ? `calc(100vw - ${getComputedStyle(drawerRef.current).width})` : '100vw'),
                }}
                style={{ padding: '0px' }}
            >
                {/* ヘッダの高さ分だけ余白を空ける */}
                <DrawerHeader ref={headerRef} />
                {/* メインコンテンツを描画 */}
                <Box sx={{
                    width: '100%',
                    height: mainContentHeight,
                    padding: 0,
                }} >
                    {switchPage()}
                </Box>
            </Box>

            <ConfirmDialog key={'settings'} title='注意' />
        </Box>
    );
}

const openedMixin = (theme: Theme): CSSObject => ({
    width: Drawer_Width,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

const CustomDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: Drawer_Width,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    })
);
